/**
 * Schedule a product's storefront window via `launch_at` / `archive_at`
 * datetime-local inputs. Round-trips ISO-8601 UTC via scheduleUtils.
 */
import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { toLocalInput, fromLocalInput } from "./scheduleUtils";

export const ProductScheduleModal = ({ product, onClose, onSave }) => {
  const [launchAt, setLaunchAt] = useState(toLocalInput(product.launch_at));
  const [archiveAt, setArchiveAt] = useState(toLocalInput(product.archive_at));

  const handleSave = () => {
    if (launchAt && archiveAt && new Date(archiveAt) <= new Date(launchAt)) {
      toast.error("Archive date must be after launch date.");
      return;
    }
    onSave(fromLocalInput(launchAt), fromLocalInput(archiveAt));
  };

  return (
    <div
      data-testid="schedule-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md border border-[#C5A059] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 bg-[#0A192F] text-white">
          <div className="font-accent text-xs tracking-wider">SCHEDULE · {product.name}</div>
          <button data-testid="schedule-modal-close-btn" onClick={onClose} className="text-white/70 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Set when this product appears on the storefront. Leave blank to disable.
            <br />Times are in your local timezone — converted to UTC on save.
          </p>

          <div>
            <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">
              LAUNCH AT (HIDDEN UNTIL)
            </label>
            <input
              data-testid="schedule-launch-input"
              type="datetime-local"
              value={launchAt}
              onChange={(e) => setLaunchAt(e.target.value)}
              className="w-full text-sm p-2.5 border border-slate-300 focus:border-[#C5A059] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">
              ARCHIVE AT (HIDDEN AFTER)
            </label>
            <input
              data-testid="schedule-archive-input"
              type="datetime-local"
              value={archiveAt}
              onChange={(e) => setArchiveAt(e.target.value)}
              className="w-full text-sm p-2.5 border border-slate-300 focus:border-[#C5A059] focus:outline-none"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-accent text-slate-600 hover:text-[#0A192F] border border-slate-300"
            >
              CANCEL
            </button>
            <button
              data-testid="schedule-clear-btn"
              onClick={() => { setLaunchAt(""); setArchiveAt(""); }}
              className="px-4 py-2 text-xs font-accent text-slate-600 hover:text-[#0A192F] border border-slate-300"
            >
              CLEAR
            </button>
            <button
              data-testid="schedule-save-btn"
              onClick={handleSave}
              className="flex-1 px-4 py-2 text-xs font-accent text-white bg-[#0A192F] hover:bg-[#0A192F]/90"
            >
              SAVE SCHEDULE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
